import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import AgeGate from "./components/AgeGate";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Stacks from "./pages/Stacks";
import StackPage from "./pages/StackPage";
import CategoryPage from "./pages/CategoryPage";
import About from "./pages/About";
import FAQs from "./pages/FAQs";
import RefundPolicy from "./pages/RefundPolicy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import VenmoPending from "./pages/VenmoPending";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Wholesale from "./pages/Wholesale";
import WholesaleShop from "./pages/WholesaleShop";
import WholesaleProductDetail from "./pages/WholesaleProductDetail";
import ContactUs from "./pages/ContactUs";
import Admin from "./pages/Admin";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import PreLaunchBanner from "./components/PreLaunchBanner";
import ExitIntent from "./components/ExitIntent";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";
import { Navigate, useParams } from "react-router-dom";
import "./App.css";

// Detect search-engine / AI crawler user-agents. When a bot is crawling, we
// suppress the AgeGate overlay so Googlebot's rendering pass sees the actual
// shop / product HTML underneath instead of a "Restricted Access" modal —
// which is what triggered the Sep 2026 Soft 404 flag on /shop in Search
// Console. The gate remains fully enforced for every real human visitor.
// Server-side compliance (payment/checkout paths) still requires an
// authenticated 21+ account regardless of what the client renders.
const BOT_UA_PATTERN = /(bot|crawler|spider|slurp|bingpreview|googlebot|adsbot|mediapartners|duckduckbot|yandex|baiduspider|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|whatsapp|telegrambot|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-web|anthropic-ai|perplexitybot|perplexity-user|applebot|google-extended|cohere-ai|meta-externalagent|bytespider|prerender|headlesschrome|lighthouse|pagespeed)/i;
function isCrawler() {
  if (typeof navigator === "undefined") return false;
  return BOT_UA_PATTERN.test(navigator.userAgent || "");
}

// Legacy /blog/* → /research-library/* permanent redirect (preserves any
// external inbound links from the pre-rename era).
function LegacyBlogRedirect() {
  return <Navigate to="/research-library" replace />;
}
function LegacyBlogPostRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/research-library/${slug}`} replace />;
}

function Shell() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#00C3FF] font-mono-tech text-sm tracking-[0.2em] uppercase">
        Initializing…
      </div>
    );
  }

  return (
    <>
      {/* Routes ALWAYS mount so per-page SEO meta populates <head> for Googlebot.
          AgeGate renders as a modal overlay on top when user isn't authenticated
          — humans still have to verify 21+ before interacting with the store.
          Nav / Footer render for authenticated users AND for crawlers, so
          Googlebot can see the full site structure (internal links, legal
          footer, address) instead of a stripped-down page that gets flagged
          as thin content or Soft 404. */}
      <div data-testid="app-shell">
        {user && <PreLaunchBanner />}
        {(user || isCrawler()) && <Nav />}
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/stacks" element={<Stacks />} />
            <Route path="/stacks/:slug" element={<StackPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/wholesale" element={<Wholesale />} />
            <Route path="/wholesale-shop" element={<WholesaleShop />} />
            <Route path="/wholesale-shop/:slug" element={<WholesaleProductDetail />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/research-library" element={<Blog />} />
            <Route path="/research-library/:slug" element={<BlogPost />} />
            {/* Legacy redirects — kept so any indexed /blog/* URLs 301 to the canonical path. */}
            <Route path="/blog" element={<LegacyBlogRedirect />} />
            <Route path="/blog/:slug" element={<LegacyBlogPostRedirect />} />
            <Route path="/account/orders" element={<Orders />} />
            <Route path="/account/orders/:id" element={<OrderDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/venmo-pending" element={<VenmoPending />} />
            {/* 404 catch-all — MUST be a dedicated NotFound page with
                noindex, NOT the homepage. The previous `element={<Home />}`
                catch-all caused Google Search Console to flag the site with
                "Duplicate without user-selected canonical" because every
                unknown URL served identical homepage HTML at 200 OK. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {(user || isCrawler()) && <Footer />}
        {user && <CartDrawer />}
        {user && <ExitIntent />}
        <Toaster position="top-right" theme="dark" />
      </div>
      {!user && !isCrawler() && <AgeGate />}
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CartProvider>
            <Shell />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
